/**
 * Self-check (responsive, no-gradient, professional):
 * - Solid surfaces only — no gradients, no decorative glows.
 * - Theme tokens drive every color/space/radius/shadow/typography choice.
 * - Breakpoints target mobile (<768) and desktop (>=769); no horizontal overflow.
 * - Motion respects prefers-reduced-motion.
 */

import styled, { css, keyframes } from "styled-components";

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background.app};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
`;

export const Container = styled.div`
  width: min(100%, 1280px);
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(3)}
    ${({ theme }) => theme.spacing(10)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  flex: 1;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(2)}
      ${({ theme }) => theme.spacing(8)};
    gap: ${({ theme }) => theme.spacing(3)};
  }
`;

export const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const PageHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const Kicker = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent.primary};
`;

export const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: clamp(32px, 3.8vw, 44px);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.1;
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 560px;
`;

export const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 800;
  }
`;

/* ----- Command bar ----- */

export const CommandBar = styled.section`
  position: sticky;
  top: ${({ theme }) => theme.spacing(2)};
  z-index: ${({ theme }) => theme.zIndex.sticky};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    padding: ${({ theme }) => theme.spacing(1.5)};
    gap: ${({ theme }) => theme.spacing(1.25)};
  }
`;

export const CommandTopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: ${({ theme }) => theme.spacing(1.5)};
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const SearchField = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0 ${({ theme }) => theme.spacing(2)};
  height: ${({ theme }) => theme.spacing(6.5)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  transition: border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.state.selected};
  }

  svg.leading {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.input.placeholder};
  }
`;

export const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: none;
  background: ${({ theme }) => theme.colors.state.hover};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  flex-shrink: 0;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.state.active};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const KbdHint = styled.kbd`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `2px ${theme.spacing(0.75)}`};
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 11px;
  font-weight: 700;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

export const SortMenu = styled.div`
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const SortButton = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  height: ${({ theme }) => theme.spacing(6.5)};
  padding: 0 ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ $open, theme }) =>
    $open ? theme.colors.state.selected : theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid
    ${({ $open, theme }) =>
      $open ? theme.colors.accent.primary : theme.colors.border.subtle};
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

export const SortPopover = styled.div`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing(1)});
  right: 0;
  min-width: 220px;
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.background.elevated};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  box-shadow: ${({ theme }) => theme.colors.shadow.high};
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

export const SortItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.5)}`};
  border: none;
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.state.selected : "transparent"};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.accent.primary : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.state.hover};
  }

  svg {
    margin-left: auto;
    color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

export const ViewToggle = styled.div`
  display: inline-flex;
  height: ${({ theme }) => theme.spacing(6.5)};
  padding: 4px;
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};

  @media (max-width: 768px) {
    display: none;
  }
`;

export const ViewToggleBtn = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: 0 ${({ theme }) => theme.spacing(1.5)};
  border: none;
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.background.surface : "transparent"};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.accent.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;
  box-shadow: ${({ $active, theme }) =>
    $active ? theme.colors.shadow.low : "none"};
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
`;

export const ChipRowLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-right: ${({ theme }) => theme.spacing(0.5)};
`;

export const Chip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
  min-height: ${({ theme }) => theme.spacing(4.5)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.accent.primary : theme.colors.background.surfaceAlt};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.primary};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.accent.primaryHover : theme.colors.border.subtle};

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  svg {
    font-size: 14px;
  }
`;

export const ResetLink = styled.button`
  margin-left: auto;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.link};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

/* ----- Result bar ----- */

export const ResultsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const ResultsCount = styled.span`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 800;
  }
`;

/* ----- Product grid / list ----- */

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(2.5)};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing(2)};
  }
`;

export const ListStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const Card = styled.article<{ $variant?: "grid" | "list" }>`
  position: relative;
  display: ${({ $variant }) => ($variant === "list" ? "grid" : "flex")};
  grid-template-columns: ${({ $variant }) =>
    $variant === "list" ? "240px minmax(0, 1fr)" : "none"};
  flex-direction: ${({ $variant }) => ($variant === "list" ? "unset" : "column")};
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  transition: transform ${({ theme }) => theme.motion.base} ease,
    box-shadow ${({ theme }) => theme.motion.base} ease,
    border-color ${({ theme }) => theme.motion.base} ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.colors.shadow.medium};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  &:focus-within {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.state.selected},
      ${({ theme }) => theme.colors.shadow.medium};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: ${({ $variant }) =>
      $variant === "list" ? "140px minmax(0, 1fr)" : "none"};
  }
`;

export const CardMedia = styled.div<{ $variant?: "grid" | "list" }>`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  aspect-ratio: ${({ $variant }) => ($variant === "list" ? "4 / 3" : "4 / 3")};
`;

export const CardImage = styled.div<{ $src: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  transition: transform ${({ theme }) => theme.motion.slow}
    cubic-bezier(0.22, 1, 0.36, 1);

  ${Card}:hover & {
    transform: scale(1.05);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    ${Card}:hover & {
      transform: none;
    }
  }
`;

export const BadgeStack = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1.25)};
  left: ${({ theme }) => theme.spacing(1.25)};
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  max-width: calc(100% - ${({ theme }) => theme.spacing(2.5)});
`;

export const UrgencyBadge = styled.span<{ $tone: "warning" | "info" }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${({ theme }) => `4px ${theme.spacing(1)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${({ $tone, theme }) =>
    $tone === "warning" ? theme.colors.accent.danger : theme.colors.accent.info};
  color: ${({ theme }) => theme.colors.text.inverse};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
`;

export const QuickAddBtn = styled.button<{ $disabled?: boolean }>`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1.25)};
  right: ${({ theme }) => theme.spacing(1.25)};
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.colors.text.disabled : theme.colors.accent.primary};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    transform: scale(1.06);
    background: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
  }

  svg {
    font-size: 18px;
  }
`;

export const CardBody = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  flex: 1;
`;

export const CardTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: clamp(16px, 1.2vw, 18px);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Price = styled.span`
  font-size: clamp(16px, 1.3vw, 20px);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.accent.primary};
  letter-spacing: -0.01em;
  white-space: nowrap;

  small {
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;
    margin-left: 2px;
  }
`;

export const CardDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const TagRow = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Tag = styled.li<{ $tone?: "neutral" | "accent" | "warning" }>`
  padding: 3px ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ $tone, theme }) =>
    $tone === "accent"
      ? theme.colors.state.selected
      : $tone === "warning"
      ? theme.colors.state.hover
      : theme.colors.background.surfaceAlt};
  color: ${({ $tone, theme }) =>
    $tone === "accent"
      ? theme.colors.accent.primary
      : $tone === "warning"
      ? theme.colors.accent.warning
      : theme.colors.text.secondary};
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === "accent"
        ? theme.colors.accent.primary
        : theme.colors.border.subtle};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const CardFooter = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  padding-top: ${({ theme }) => theme.spacing(1)};
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;

export const StockIndicator = styled.span<{ $state: "in" | "low" | "out" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  color: ${({ $state, theme }) =>
    $state === "out"
      ? theme.colors.text.disabled
      : $state === "low"
      ? theme.colors.accent.warning
      : theme.colors.accent.primary};

  &::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: currentColor;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.background.surface};
  }
`;

export const CardCta = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.75)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: 1px solid ${({ theme }) => theme.colors.accent.primary};
  background: ${({ $disabled, theme }) =>
    $disabled ? "transparent" : theme.colors.accent.primary};
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.colors.text.disabled : theme.colors.text.inverse};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accent.primaryHover};
    transform: translateY(-1px);
  }
`;

export const EditLinkBtn = styled.button`
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.25)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.accent.primary};
    background: ${({ theme }) => theme.colors.state.hover};
  }
`;

/* ----- Skeleton ----- */

const skeletonBg = css`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background.surfaceAlt} 0%,
    ${({ theme }) => theme.colors.background.surface} 50%,
    ${({ theme }) => theme.colors.background.surfaceAlt} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s infinite linear;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
`;

export const SkeletonMedia = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  ${skeletonBg}
`;

export const SkeletonBody = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const SkeletonLine = styled.div<{ $width?: number; $height?: number }>`
  height: ${({ $height = 12 }) => `${$height}px`};
  width: ${({ $width = 100 }) => `${$width}%`};
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  ${skeletonBg}
`;

/* ----- Empty state ----- */

export const EmptyState = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(8)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px dashed ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  text-align: center;
`;

export const EmptyIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.state.hover};
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: 28px;
`;

export const EmptyTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h3};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const EmptyCopy = styled.p`
  margin: 0;
  max-width: 420px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.body};
`;

/* ----- Recently viewed ----- */

export const RecentlyViewedSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;

export const RecentlyViewedHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const RecentlyViewedTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const RecentlyViewedScroll = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  overflow-x: auto;
  padding-bottom: ${({ theme }) => theme.spacing(1)};
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) =>
    `${theme.colors.border.default} transparent`};

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 999px;
  }
`;

export const RecentlyViewedCard = styled.button`
  flex: 0 0 180px;
  scroll-snap-align: start;
  text-align: left;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  background: ${({ theme }) => theme.colors.background.surface};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  overflow: hidden;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
  }

  img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    display: block;
  }
`;

export const RecentlyViewedMeta = styled.div`
  padding: ${({ theme }) => theme.spacing(1.25)};
  display: flex;
  flex-direction: column;
  gap: 4px;

  p {
    margin: 0;
  }

  p.name {
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p.price {
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 800;
    color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

/* ----- Error banner ----- */

export const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.state.hover};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  font-size: ${({ theme }) => theme.typography.body};

  svg {
    color: ${({ theme }) => theme.colors.accent.warning};
    flex-shrink: 0;
  }
`;

/* ----- Modal ----- */

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.modalOverlay};
  background: ${({ theme }) => theme.colors.state.backdrop};
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(3)};
`;

export const ModalFrame = styled.div`
  position: relative;
  width: min(100%, 760px);
  max-height: 90vh;
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  box-shadow: ${({ theme }) => theme.colors.shadow.high};
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: 240px auto;
  }
`;

export const ModalMedia = styled.div`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
`;

export const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ModalClose = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1.5)};
  right: ${({ theme }) => theme.spacing(1.5)};
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  font-size: 18px;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.state.hover};
    transform: scale(1.06);
  }
`;

export const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  overflow-y: auto;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: clamp(22px, 2.2vw, 28px);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ModalPrice = styled.p`
  margin: 0;
  font-size: clamp(22px, 2vw, 28px);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.accent.primary};
  letter-spacing: -0.01em;

  small {
    font-size: ${({ theme }) => theme.typography.body};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;
    margin-left: 4px;
  }
`;

export const ModalDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
`;

export const ModalDetailRow = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};

  dt {
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  dd {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.body};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding-top: ${({ theme }) => theme.spacing(1)};
`;

export const QuantityPicker = styled.div`
  display: inline-flex;
  align-items: center;
  height: ${({ theme }) => theme.spacing(6)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.surface};
  overflow: hidden;

  button {
    width: 44px;
    height: 100%;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.state.hover};
    }

    &:disabled {
      color: ${({ theme }) => theme.colors.text.disabled};
      cursor: not-allowed;
    }
  }

  span {
    min-width: 32px;
    text-align: center;
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 800;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;
